<script setup>
import { onMounted, ref, computed } from 'vue';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { getCountryOptions } from 'dashboard/helper/countries';

const props = defineProps({
  contactReasonPath: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'created']);

const store = useStore();
const { t } = useI18n();

const reasonName = ref('');
const reasonDescription = ref('');
const reasonColor = ref('#9f9f9f');
const targetMarkets = ref([]);
const targetInboxes = ref([]);
const sopUrls = ref([]);
const allCountryOptions = getCountryOptions();

const inboxes = useMapGetter('inboxes/getInboxes');

const rules = {
  reasonName: { required },
  reasonDescription: { required },
};
const v$ = useVuelidate(rules, {
  reasonName,
  reasonDescription,
});

const uiFlags = useMapGetter('contactReasons/getUIFlags');
const parentContactReason = useFunctionGetter(
  'contactReasons/getContactReasonByPath',
  props.contactReasonPath
);

const addContactReason = async () => {
  try {
    const newContactReason = await store.dispatch('contactReasons/create', {
      contactReasonPath: props.contactReasonPath,
      data: {
        name: reasonName.value,
        description: reasonDescription.value,
        level: props.contactReasonPath.length + 1,
        parent_id: parentContactReason.value
          ? parentContactReason.value.id
          : null,
        config: {
          color: reasonColor.value,
          target_markets: targetMarkets.value,
          target_inboxes: targetInboxes.value,
          sop_urls: sopUrls.value.filter(e => e.market && e.url),
        },
      },
    });
    emit('created', newContactReason);
    emit('close');
    useAlert(t('CONTACT_REASONS.API.ADD.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACT_REASONS.API.ADD.ERROR_MESSAGE'));
  }
};

const marketOptions = computed(() => {
  return [...new Set(inboxes.value.map(inbox => inbox.market))];
});

const inboxesOptions = computed(() => inboxes.value);

const marketMenuItems = computed(() =>
  marketOptions.value.map(m => ({ value: m, label: m }))
);

const inboxMenuItems = computed(() =>
  inboxesOptions.value.map(i => ({ value: i.id, label: i.name }))
);

const addSopUrl = () => sopUrls.value.push({ market: '', url: '' });
const removeSopUrl = index => sopUrls.value.splice(index, 1);

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        contactReasonPath.length > 0
          ? $t('CONTACT_REASONS.FORM.TITLE_WITH_PARENT', {
              name: parentContactReason.name,
            })
          : $t('CONTACT_REASONS.FORM.TITLE')
      "
      :header-content="$t('CONTACT_REASONS.FORM.DESC')"
    />
    <form
      class="flex flex-col items-start w-full"
      @submit.prevent="addContactReason"
    >
      <div class="w-full">
        <label :class="{ error: v$.reasonName.$error }">
          {{ $t('CONTACT_REASONS.FORM.NAME.LABEL') }}
          <input
            v-model="reasonName"
            type="text"
            :placeholder="$t('CONTACT_REASONS.FORM.NAME.PLACEHOLDER')"
            @input="v$.reasonName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.reasonDescription.$error }">
          {{ $t('CONTACT_REASONS.FORM.DESCRIPTION.LABEL') }}
          <input
            v-model="reasonDescription"
            type="text"
            :placeholder="$t('CONTACT_REASONS.FORM.DESCRIPTION.PLACEHOLDER')"
            @input="v$.reasonDescription.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ $t('CONTACT_REASONS.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="reasonColor" />
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ $t('CONTACT_REASONS.FORM.TARGET_MARKETS.LABEL') }}
          <TagMultiSelectComboBox
            v-model="targetMarkets"
            :options="marketMenuItems"
            :placeholder="$t('CONTACT_REASONS.FORM.TARGET_MARKETS.PLACEHOLDER')"
          />
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ $t('CONTACT_REASONS.FORM.TARGET_INBOXES.LABEL') }}
          <TagMultiSelectComboBox
            v-model="targetInboxes"
            :options="inboxMenuItems"
            :placeholder="$t('CONTACT_REASONS.FORM.TARGET_INBOXES.PLACEHOLDER')"
          />
        </label>
      </div>

      <div class="w-full">
        <label class="block mb-1">
          {{ $t('CONTACT_REASONS.FORM.SOP_URLS.LABEL') }}
        </label>
        <div class="flex flex-col gap-2">
          <div
            v-for="(entry, index) in sopUrls"
            :key="index"
            class="flex items-center gap-2"
          >
            <div class="w-44 shrink-0">
              <ComboBox
                v-model="entry.market"
                :options="allCountryOptions"
                :placeholder="
                  $t('CONTACT_REASONS.FORM.SOP_URLS.MARKET_PLACEHOLDER')
                "
                :search-placeholder="
                  $t('CONTACT_REASONS.FORM.SOP_URLS.MARKET_SEARCH')
                "
              />
            </div>
            <input
              v-model="entry.url"
              type="text"
              class="flex-1 !mb-0"
              :placeholder="$t('CONTACT_REASONS.FORM.SOP_URLS.URL_PLACEHOLDER')"
            />
            <Button
              icon="i-lucide-trash-2"
              faded
              slate
              type="button"
              @click="removeSopUrl(index)"
            />
          </div>
        </div>
        <Button
          icon="i-lucide-plus"
          faded
          slate
          type="button"
          size="small"
          :label="$t('CONTACT_REASONS.FORM.SOP_URLS.ADD')"
          class="mt-2"
          @click="addSopUrl"
        />
        <p class="text-xs text-n-slate-11 mt-1">
          {{ $t('CONTACT_REASONS.FORM.SOP_URLS.HELPER') }}
        </p>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CONTACT_REASONS.FORM.CANCEL')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('CONTACT_REASONS.FORM.CREATE')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
